'use client'

import { useEffect, type ReactNode } from 'react'

type NodeMethod = 'appendChild' | 'insertBefore' | 'removeChild' | 'replaceChild'

type PatchedNodePrototype = Node & {
  __martiniExternalDomMutationGuard?: {
    refCount: number
    methods: Pick<Node, NodeMethod>
  }
}

function isNotFoundError(error: unknown) {
  return error instanceof DOMException && error.name === 'NotFoundError'
}

function patchNodePrototype() {
  const prototype = Node.prototype as PatchedNodePrototype
  const existingGuard = prototype.__martiniExternalDomMutationGuard

  if (existingGuard) {
    existingGuard.refCount += 1
    return
  }

  const originalMethods = {
    appendChild: prototype.appendChild,
    insertBefore: prototype.insertBefore,
    removeChild: prototype.removeChild,
    replaceChild: prototype.replaceChild,
  }

  prototype.__martiniExternalDomMutationGuard = {
    refCount: 1,
    methods: originalMethods,
  }

  prototype.removeChild = function removeChild<T extends Node>(child: T): T {
    try {
      return originalMethods.removeChild.call(this, child) as T
    } catch (error) {
      if (isNotFoundError(error) && child.parentNode !== this) {
        return child
      }
      throw error
    }
  }

  prototype.insertBefore = function insertBefore<T extends Node>(newChild: T, refChild: Node | null): T {
    try {
      return originalMethods.insertBefore.call(this, newChild, refChild) as T
    } catch (error) {
      if (isNotFoundError(error) && refChild && refChild.parentNode !== this) {
        return originalMethods.appendChild.call(this, newChild) as T
      }
      throw error
    }
  }

  prototype.replaceChild = function replaceChild<T extends Node>(newChild: Node, oldChild: T): T {
    try {
      return originalMethods.replaceChild.call(this, newChild, oldChild) as T
    } catch (error) {
      if (isNotFoundError(error) && oldChild.parentNode !== this) {
        originalMethods.appendChild.call(this, newChild)
        return oldChild
      }
      throw error
    }
  }
}

function unpatchNodePrototype() {
  const prototype = Node.prototype as PatchedNodePrototype
  const guard = prototype.__martiniExternalDomMutationGuard

  if (!guard) return

  guard.refCount -= 1
  if (guard.refCount > 0) return

  prototype.insertBefore = guard.methods.insertBefore
  prototype.removeChild = guard.methods.removeChild
  prototype.replaceChild = guard.methods.replaceChild
  delete prototype.__martiniExternalDomMutationGuard
}

export function ExternalDomMutationGuard({ children }: { children: ReactNode }) {
  useEffect(() => {
    patchNodePrototype()
    return unpatchNodePrototype
  }, [])

  return <>{children}</>
}
